import manifoldCSSReset from '@manifoldxyz/css-reset';
import { DATA_WIDGET_VALUE } from '@/common/constants';
import { getAllChildren, parseJSONProp } from '@/common/functions';
import { DataWidgetName, Widgets } from '@/exported';
// import MCampaignWidget from '@/exports/MCampaignWidget.vue';
// import {
//   MCampaignWidgetOptionsProps,
//   MCampaignWidgetOptionsPropsType
// } from '@/exports/MCampaignWidgetProps';
import { renderComponentWithApp } from '../build/mount';

// track app-component Vue instances for proper destroying later
const renderedComponents = new Map();
let widgetAttributeChangeObserver: MutationObserver;
let bodyChangeObserver: MutationObserver;

/* Mutation Handlers */

const handleWidgetAttributeChange = async (mutations: MutationRecord[]) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes' && mutation.attributeName !== 'data-v-app') {
      // destroy pre-existing app-component before replacing it with new one
      destroyPotentialWidget(mutation.target as HTMLElement);
      replaceWithWidget(mutation.target as HTMLElement);
    }
  });
};

const handleDynamicDivs = async (mutations: MutationRecord[]) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      const htmlEl = node as HTMLElement;

      if (htmlEl?.dataset?.widget === DATA_WIDGET_VALUE) {
        replaceWithWidget(htmlEl);
        return;
      }

      const children = getAllChildren(htmlEl);
      children.pop(); // last element is root el which we've already checked above

      children.forEach((child) => {
        if ((child as HTMLElement)?.dataset?.widget === DATA_WIDGET_VALUE) {
          replaceWithWidget(child as HTMLElement);
        }
      });
    });

    mutation.removedNodes.forEach((node) => {
      const htmlEl = node as HTMLElement;

      if (htmlEl?.dataset?.widget === DATA_WIDGET_VALUE) {
        destroyPotentialWidget(htmlEl);
        return;
      }

      const children = getAllChildren(htmlEl);
      children.forEach((child) => {
        if ((child as HTMLElement)?.dataset?.widget === DATA_WIDGET_VALUE) {
          destroyPotentialWidget(child as HTMLElement);
        }
      });
    });
  });
};

/* Render and Destroy */

/**
 * This function gets called when a div with a data-widget attribute is found in the DOM.
 * It kicks off the chain of events to replace the div with the actual Vue Widget.
 * @param el - is the full div with the data-widget attribute
 */
const replaceWithWidget = (el: HTMLElement) => {
  // grab the DOM element's data attributes to use as propsData
  const data = el.dataset;
  const widgetName = data.widget as DataWidgetName;

  // parse all relevant data-attrs and typecast them into props
  const props = new Widgets[widgetName].props({});
  const propsKeys = Object.keys(props);
  const propsData: Record<string, unknown> = {};
  const propDefaults = Widgets[widgetName].defaults;

  propsKeys.forEach((key) => {
    const propDefinition: any = propDefaults[key as keyof typeof propDefaults];

    /*
     * since dataset is always a string array, we need to do some
     * typecasting and inferencing here to set propsData[key] properly
     */
    if (!data[key]) {
      propsData[key] = propDefinition.default;
      return;
    }

    // if (propDefinition.type === String) {
    if (typeof propDefinition.default == 'string') {
      propsData[key] = data[key];
      return;
    }

    // If an array type like we use for addTpCartEmojis, pass the prop directly through
    if (Array.isArray(propDefinition.default)) {
      propsData[key] = [data[key]];
      return;
    }

    // if (propDefinition.type === Number) { // was
    if (typeof propDefinition.default == 'number') {
      propsData[key] = JSON.parse(data[key] as string);
      return;
    }

    // Boolean props
    // propsData[key] = parseJSONProp(data[key], key, propDefinition.type);
    propsData[key] = parseJSONProp(data[key], key, propDefinition.default);
  });

  // render the component
  const renderedComponent = renderComponentWithApp({
    el: el,
    component: Widgets[widgetName].wrapper,
    props: propsData
  });
  renderedComponents.set(el, renderedComponent);

  // observe any attribute changes and rerender accordingly
  const config = {
    attributes: true,
    childList: false,
    subtree: false
  };
  widgetAttributeChangeObserver.observe(el, config);
};

const destroyPotentialWidget = (el: HTMLElement) => {
  const renderedComponentRef = renderedComponents.get(el);
  if (renderedComponentRef) {
    // unmount and destroy the pre-existing Vue app-component for memory's sake
    renderedComponentRef();
    renderedComponents.delete(el);
  }
};

/* Main Script -- Entry point*/
if (window) {
  manifoldCSSReset();

  window.addEventListener('load', () => {
    // MutationObserver gets reused by every rendered component
    widgetAttributeChangeObserver = new window.MutationObserver(handleWidgetAttributeChange);

    // Sweep the DOM for pre-existing widget divs that need replacing
    const elements = Object.keys(Widgets)
      .map((dataWidgetName) => {
        return [...document.querySelectorAll(`[data-widget=${dataWidgetName}]`)];
      })
      .flat();

    elements.forEach((el: Element) => {
      // Found a div that needs to become a widget. Kick off converting it.
      replaceWithWidget(el as HTMLElement);
    });

    // listen for new widget divs being added to the document that need replacing
    const bodyNode = document.querySelector('body');
    if (bodyNode && !bodyChangeObserver) {
      const config = {
        childList: true,
        subtree: true
      };
      bodyChangeObserver = new window.MutationObserver(handleDynamicDivs);
      bodyChangeObserver.observe(bodyNode, config);
    }
  });
}
